import React from 'react';
import type { AttendanceActionView } from '../../../../services/attendance/attendance.service';
import classes from './attendance-slip.module.scss';

export type AttendanceSlipProps = {
  action: AttendanceActionView;
  innerRef?: React.Ref<HTMLDivElement>;
  // The offscreen capture copy renders the same markup, so it drops test ids to keep locators unique.
  suppressTestIds?: boolean;
};

const pad = (value: number): string => String(value).padStart(2, '0');

const slipDate = (value: Date): string => `${pad(value.getDate())}.${pad(value.getMonth() + 1)}.${value.getFullYear()}`;

const slipTime = (value: Date, withSeconds = true): string =>
  `${pad(value.getHours())}:${pad(value.getMinutes())}${withSeconds ? `:${pad(value.getSeconds())}` : ''}`;

const durationLabel = (minutes: number): string => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

// Summary: Printable attendance confirmation (אישור נוכחות) for a clock-in / clock-out action.
// Shared by the pre-print preview and the offscreen node captured for the printer.
export const AttendanceSlip: React.FC<AttendanceSlipProps> = ({
  action,
  innerRef,
  suppressTestIds = false,
}) => {
  const testId = (id: string): string | undefined => (suppressTestIds ? undefined : id);
  const isClockOut = action.actionType === 'clock_out';
  const actionAt = new Date(action.actionDate);
  const printedAt = new Date();
  const { clockInAt, durationMinutes } = action;
  const showShiftFacts = isClockOut && Boolean(clockInAt) && durationMinutes !== undefined;

  return (
    <div ref={innerRef} className={classes.slip} data-testid={testId('attendance-slip')}>
      <div className={classes.brand}>COMMIT AI</div>
      <div className={classes.subtitle}>אישור נוכחות</div>

      <div className={classes.rule} />

      <div className={classes.banner} data-testid={testId('attendance-slip-action')}>
        {isClockOut ? 'יציאה ממשמרת' : 'כניסה למשמרת'}
      </div>

      <div className={classes.employeeName} data-testid={testId('attendance-slip-employee')}>
        {action.employeeName || '\u00a0'}
      </div>
      <div className={classes.date}>{slipDate(actionAt)}</div>

      {showShiftFacts && clockInAt && (
        <>
          <div className={classes.dotted} />

          <div className={classes.shiftPair}>
            <div className={classes.shiftPoint} data-testid={testId('attendance-print-clock-in-at')}>
              <div className={classes.shiftPointLabel}>כניסה</div>
              <div className={classes.shiftPointTime}>{slipTime(new Date(clockInAt))}</div>
              <div className={classes.shiftPointDate}>{slipDate(new Date(clockInAt))}</div>
            </div>
            <div className={classes.shiftArrow} aria-hidden="true">&gt;</div>
            <div className={classes.shiftPoint}>
              <div className={classes.shiftPointLabel}>יציאה</div>
              <div className={classes.shiftPointTime}>{slipTime(actionAt)}</div>
              <div className={classes.shiftPointDate}>{slipDate(actionAt)}</div>
            </div>
          </div>
        </>
      )}

      <div className={classes.dotted} />

      {showShiftFacts && durationMinutes !== undefined && (
        <div className={classes.totalBox} data-testid={testId('attendance-print-total-duration')}>
          <span className={classes.totalLabel}>סה"כ שעות במשמרת</span>
          <span className={classes.totalValue}>{durationLabel(durationMinutes)}</span>
        </div>
      )}

      <div className={classes.printedAt}>
        הודפס {slipDate(printedAt)} {slipTime(printedAt, false)}
      </div>
    </div>
  );
};
